import styled from "@emotion/styled";
import React from "react";
import { useCallbackRef } from "../../../hooks/useCallbackRef";
import { useScreenWidth } from "../../../hooks/useScreenWidth";
import { CareMomentRank, CareMomentRankChange, Midpoint } from "../CareMoments.types";
import {
  calculateProgressIncrement,
  calculateVerticalCurvedLineLength,
  getControlPointsForCurvedVerticalLine,
  getRoadFromAndToPercentage,
  increaseProgress,
} from "../CareMoments.utils";

const STEP_DURATION_SECONDS = 3;

export default function RoadProgress(props: {
  scaffoldRef: React.RefObject<HTMLDivElement>;
  numSegments: number;
  rankChange: CareMomentRankChange;
  ranks: CareMomentRank[];
  avatarUrl: string | null;
  onFinishAnimation: () => void;
}) {
  const svgNS = "http://www.w3.org/2000/svg";
  const svgRef = React.useRef<SVGSVGElement>(null);
  const avatarRef = React.useRef<HTMLDivElement>(null);
  const midpointsRef = React.useRef<Midpoint[]>([]);
  const screenWidth = useScreenWidth();
  const segmentWidth = screenWidth;
  const segmentHeight = 100;
  const initialVerticalOffset = 25;
  const pathWidth = 16;
  const curveStrength = 80; // Adjust this value to increase or decrease the curve

  const onFinishAnimationRef = useCallbackRef(props.onFinishAnimation);

  const createSvgPathData = React.useCallback(
    (numSegments: number): string => {
      let d = `M0,${initialVerticalOffset}`; // Start at the point (0, initialVerticalOffset)
      let x = 0,
        y = initialVerticalOffset;
      let direction = 1; // Direction of the horizontal line (1 for right, -1 for left)

      // Adjust the starting x-coordinate to account for the curve that will be generated
      x += curveStrength; // This ensures that the curve does not go into negative x-coordinates

      for (let i = 0; i < numSegments; i++) {
        // Draw horizontal line
        x += direction * (segmentWidth - 2 * curveStrength); // Subtract the curveStrength from the segmentWidth to prevent clipping
        d += ` H${x}`;

        if (i === numSegments - 1) {
          break;
        }

        // Calculate control points for the curve
        const {
          cx1,
          cy1,
          cx2,
          cy2,
          y: newY,
        } = getControlPointsForCurvedVerticalLine({
          curveStrength,
          direction,
          segmentHeight,
          x,
          y,
        });
        y = newY;

        // Draw a curve that arcs out from the center
        d += ` C${cx1},${cy1} ${cx2},${cy2} ${x},${y}`;

        // Change direction for the next horizontal line
        direction *= -1;

        // Estimate midpoint of the curve and add to midpoints array
        const midX = (cx1 + cx2) / 2;
        const midY = (cy1 + cy2) / 2;
        const midXoffset = 20 * direction; // Offset the midpoint slightly to the right or left

        midpointsRef.current.push({ x: midX + midXoffset, y: midY });
      }

      return d;
    },
    [segmentWidth]
  );

  const createCircle = (x: number, y: number, radius: number, id: string) => {
    const circle = document.createElementNS(svgNS, "circle");
    circle.setAttribute("cx", x.toString());
    circle.setAttribute("cy", y.toString());
    circle.setAttribute("r", radius.toString());
    circle.setAttribute("fill", "#3C83F6");
    circle.setAttribute("id", id);
    return circle;
  };

  const buildSvg = React.useCallback(
    ({ midpoints, svg }: { svg: SVGSVGElement; midpoints: Midpoint[] }) => {
      svg.setAttribute(
        "height",
        (props.numSegments * segmentHeight + initialVerticalOffset).toString()
      );
      svg.setAttribute("xmlns", svgNS);

      const path = document.createElementNS(svgNS, "path");
      path.setAttribute("fill", "none");
      path.setAttribute("id", "roadPath");
      path.setAttribute("stroke", "#BEDBFE");
      path.setAttribute("d", createSvgPathData(props.numSegments));
      path.setAttribute("stroke-width", pathWidth.toString());
      svg.appendChild(path);

      const totalLength = path.getTotalLength();

      // Create the progressPath with the same 'd' attribute as roadPath
      const progressPath = path.cloneNode() as SVGPathElement;
      progressPath.setAttribute("stroke", "#3C83F6");
      progressPath.setAttribute("id", "progressPath");
      progressPath.setAttribute("stroke-dasharray", totalLength.toString());
      progressPath.setAttribute("stroke-dashoffset", totalLength.toString());

      svg.appendChild(progressPath);

      // Create the dashed line path with the same 'd' attribute as roadPath
      const dashedLinePath = path.cloneNode() as SVGPathElement;
      dashedLinePath.setAttribute("stroke", "#3077EB");
      dashedLinePath.setAttribute("stroke-dasharray", "1,5"); // Create the dashed effect
      dashedLinePath.setAttribute("stroke-width", "1");
      dashedLinePath.setAttribute("id", "dashedLinePath");
      dashedLinePath.style.strokeLinecap = "round";

      // Add the dashed line path to the SVG
      svg.appendChild(dashedLinePath);

      const progressLinePath = dashedLinePath.cloneNode() as SVGPathElement;
      progressLinePath.setAttribute("stroke", "#BEDBFE");
      progressLinePath.setAttribute("id", "progressLinePath");
      dashedLinePath.setAttribute("stroke-dasharray", "1,5"); // Create the dashed effect
      progressLinePath.setAttribute("stroke-dashoffset", "1");

      svg.appendChild(progressLinePath);

      // Add circles to the SVG
      midpoints.forEach((midpoint, index) => {
        svg.appendChild(createCircle(midpoint.x, midpoint.y, 4, "circle" + index));
      });

      return { totalLength, progressPath };
    },
    [createSvgPathData, props.numSegments]
  );

  const paintCirclesInitially = React.useCallback(
    ({
      progressPath,
      totalLength,
      progressOffset,
    }: {
      progressPath: SVGPathElement;
      totalLength: number;
      progressOffset: number;
    }) => {
      const point = progressPath.getPointAtLength(totalLength - progressOffset);

      midpointsRef.current.forEach((midpoint, index) => {
        const circle = document.getElementById("circle" + index) as SVGCircleElement | null;

        const color = point.y >= midpoint.y - 10 ? "white" : "#3C83F6";

        circle?.setAttribute("fill", color);
      });
    },
    []
  );

  React.useEffect(() => {
    if (!svgRef.current || !avatarRef.current) return;

    const svg = svgRef.current;
    const avatar = avatarRef.current;

    const { totalLength, progressPath } = buildSvg({
      midpoints: midpointsRef.current,
      svg: svg,
    });

    const verticalCurvedLineLength = calculateVerticalCurvedLineLength({
      curveStrength,
      segmentHeight,
    });

    const { fromProgress, toProgress } = getRoadFromAndToPercentage({
      rankIds: props.ranks.map((rank) => rank.id),
      rank: props.rankChange,
      road: {
        totalLength,
        segmentWidth,
        curveStrength,
        segmentHeight,
        verticalCurvedLineLength,
      },
    });

    const direction = fromProgress < toProgress ? 1 : -1;
    const progressIncrement = calculateProgressIncrement({
      numSteps: props.ranks.length,
      stepDuration: STEP_DURATION_SECONDS * 1000,
    });

    paintCirclesInitially({
      progressPath,
      totalLength,
      progressOffset: totalLength - (fromProgress / 100) * totalLength,
    });

    let progress = fromProgress;

    const drawProgress = () => {
      if (props.scaffoldRef.current === null) {
        return;
      }

      progress = increaseProgress({
        avatar: avatar,
        progressPath: progressPath,
        totalLength: totalLength,
        direction: direction,
        progressIncrement: progressIncrement,
        toProgress: toProgress,
        scaffold: props.scaffoldRef.current,
        midpoints: midpointsRef.current,
        prevProgress: progress,
        onFinishAnimation: onFinishAnimationRef,
      });

      if (progress === 100) {
        return;
      }

      animationFrame = requestAnimationFrame(drawProgress);
    };

    let animationFrame = requestAnimationFrame(drawProgress);

    return () => {
      cancelAnimationFrame(animationFrame);

      midpointsRef.current = [];
      svg.innerHTML = "";
    };
  }, [
    buildSvg,
    onFinishAnimationRef,
    paintCirclesInitially,
    props.rankChange,
    props.ranks,
    props.scaffoldRef,
    segmentWidth,
  ]);

  return (
    <SvgContainer>
      <Avatar ref={avatarRef}>
        <AvatarImageWrapper>
          {props.avatarUrl === null && <img src={"/assets/avatar.svg"} alt="Avatar" />}
          {props.avatarUrl !== null && <AvatarImage src={props.avatarUrl} />}
        </AvatarImageWrapper>
      </Avatar>
      <svg ref={svgRef} width="100%" />
    </SvgContainer>
  );
}

const SvgContainer = styled.div`
  position: relative;
  width: 100%;
`;

const Avatar = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  position: absolute;
  background-color: #fff;
  box-shadow: 0 0 10.72px 0 rgba(0, 0, 0, 0.18);

  img {
    filter: invert(86%) sepia(44%) saturate(2917%) hue-rotate(322deg) brightness(106%)
      contrast(103%);
  }
`;

const AvatarImageWrapper = styled.div`
  padding: 2px;

  width: 100%;
  height: 100%;

  box-sizing: border-box;

  img {
    width: 100%;
    height: 100%;
    border-radius: 50%;
  }
`;

const AvatarImage = styled.div<{ src: string }>`
  background-image: url(${(props) => props.src});
  background-size: cover;
  background-position: center;
  width: 100%;
  height: 100%;
  border-radius: 50%;
`;
